import React, { useEffect, useRef, useState } from 'react'

import { Box, useTheme } from '@mui/material'
import { MuiTelInput } from 'mui-tel-input'
import { getCountries, getCountryCallingCode, parsePhoneNumberFromString } from 'libphonenumber-js'
import { SelectDrawer, SelectDrawerOption } from './SelectDrawer'
import { FIELD_HEIGHT, fieldSurfaceSx } from 'components/UI/fieldMetrics'

const codeToFlagEmoji = (code: string) =>
  String.fromCodePoint(...[...code.toUpperCase()].map(ch => 0x1f1e6 + ch.charCodeAt(0) - 65))

let regionNames: Intl.DisplayNames | undefined
try {
  regionNames = new Intl.DisplayNames(['en'], { type: 'region' })
} catch {
  regionNames = undefined
}

// Country list (emoji flag + name + dial code), built once. Sorted by name.
const COUNTRY_OPTIONS: (SelectDrawerOption & { dialCode: string })[] = getCountries()
  .map(code => {
    const dialCode = getCountryCallingCode(code)
    return {
      value: code,
      label: (regionNames?.of(code) as string) || code,
      flag: codeToFlagEmoji(code),
      secondary: `+${dialCode}`,
      dialCode,
    }
  })
  .sort((a, b) => a.label.localeCompare(b.label))

export interface PhoneFieldProps {
  value: string
  onChange: (value: string) => void
  /** ISO country code used to seed the dial code, e.g. "US". */
  defaultCountry?: string
  /** Render the error (red) border treatment. */
  error?: boolean
  fullWidth?: boolean
}

/**
 * App-level phone input. `mui-tel-input` handles formatting, but its built-in
 * (flagcdn-image) country dropdown is disabled and replaced with a compact
 * bottom-sheet country picker (`SelectDrawer`, emoji flags — no network/broken
 * images) — the mobile-native pattern. House field treatment lives here once.
 */
export const PhoneField: React.FC<PhoneFieldProps> = ({
  value,
  onChange,
  defaultCountry = 'US',
  error = false,
  fullWidth = true,
}) => {
  const theme = useTheme()
  // Seed the flag from the NUMBER, not from `defaultCountry`. Seeding from the
  // prop showed a US flag beside a "+44" dial code for every non-US number on
  // file: mui-tel-input only reports a country when the user TYPES, so a value
  // that arrives pre-filled never moved the flag off the default.
  const [country, setCountry] = useState<string>(
    () => parsePhoneNumberFromString((value ?? '').trim())?.country ?? defaultCountry,
  )

  // The lazy initial state above is not enough on its own: `value` is empty at
  // mount and arrives later from the async profile load, so the first render
  // legitimately has nothing to parse. Re-derive whenever the value changes
  // from outside. Guarded on `next !== country` so typing (which sets country
  // via MuiTelInput's onChange) is never fought by this effect.
  useEffect(() => {
    const next = parsePhoneNumberFromString((value ?? '').trim())?.country
    if (next && next !== country) setCountry(next)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value])

  // `country` drives ONLY the flag picker below — it must NOT be fed back into
  // MuiTelInput's `defaultCountry`. That prop is initial-value semantics: when
  // it changes mid-life MuiTelInput re-initialises and CLEARS the typed number
  // (observed — the input emptied to a bare "+44" the moment the effect above
  // moved the country). Freezing it to the mount value is safe because a
  // non-empty `value` is already a full E.164 string, from which MuiTelInput
  // derives the country itself; `defaultCountry` only matters while empty.
  const initialCountryRef = useRef(country)

  const brandColor = theme.palette.primary.main || (theme.palette as any).brand?.[600]
  const borderColor = error ? theme.palette.error.main : theme.palette.grey[300]
  const accentColor = error ? theme.palette.error.main : brandColor

  const handleCountrySelect = (code: string) => {
    setCountry(code)
    const dc = COUNTRY_OPTIONS.find(c => c.value === code)?.dialCode
    // Reset to the new calling code so mui-tel-input switches country + reformats.
    if (dc) onChange(`+${dc} `)
  }

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        /*
         * The shared field surface. The radius was `theme.shape.borderRadius`
         * VERBATIM — the tenant's Studio value, unclamped — so on a brand at 10
         * this rendered a 10px phone field beside 6px text fields in the same
         * drawer. MEASURED at 10px against every other field's 6. `fieldSurfaceSx`
         * applies `getFieldRadius`, which is the clamp that exists for exactly
         * this (radius does not scale with width; see FIELD_RADIUS_MAX).
         */
        ...fieldSurfaceSx(theme),
        // Error overrides the token border, and needs `!important` to beat
        // index.css's `#vera-shadow-root *` ID rule the same way the token does.
        ...(error ? { border: `1px solid ${borderColor} !important` } : null),
        '&:hover': { borderColor: accentColor },
        '&:focus-within': { borderColor: accentColor },
        width: fullWidth ? '100%' : undefined,
        // VERA-291: was hardcoded to 44 — the one field-shaped control in the
        // app that didn't match the shared Input's height, so Profile's phone
        // row (which reuses this trigger) stood 4px taller than the
        // notification drawer's other fields. See fieldMetrics.ts.
        height: FIELD_HEIGHT,
      }}
    >
      <SelectDrawer
        compact
        value={country}
        onChange={handleCountrySelect}
        options={COUNTRY_OPTIONS}
        title='Select country'
        placeholder=''
        searchable
        searchPlaceholder='Search'
      />
      <MuiTelInput
        defaultCountry={initialCountryRef.current as any}
        // NO `forceCallingCode`. It pins the dial code as a non-editable prefix,
        // so the only way to reach another country was the flag picker first —
        // you could not just type "+1…". Typing the country code is the faster
        // path and the one people reach for, especially pasting a number that
        // already carries it. Without the flag, the input accepts the whole
        // international number, mui-tel-input reports the country it parses via
        // `info.countryCode` below, and the effect above re-derives the flag —
        // so the picker still works, it just is not mandatory any more.
        disableDropdown
        value={value ?? ''}
        onChange={(v, info) => {
          onChange(v ?? '')
          if (info?.countryCode && info.countryCode !== country) setCountry(info.countryCode)
        }}
        fullWidth={fullWidth}
        sx={{
          flex: 1,
          mt: 0,
          // Hide mui-tel-input's own flag/button — the compact picker owns the flag.
          '& .MuiTelInput-IconButton, & .MuiTelInput-Flag': { display: 'none !important' },
          '& .MuiInputAdornment-root': { ml: 0 },
          '& .MuiInputBase-root.MuiOutlinedInput-root': { marginTop: '0 !important' },
          '& .MuiInputBase-root': { border: 'none !important', pl: 0 },
          '& .MuiOutlinedInput-notchedOutline': { border: 'none !important' },
          '& .MuiInputBase-input': { color: theme.palette.text.primary, fontSize: '16px' },
          '& .MuiInputBase-input::placeholder': { color: theme.palette.text.secondary },
          '& .MuiTypography-root': { color: theme.palette.text.primary },
        }}
      />
    </Box>
  )
}
